import { useMemo, useState } from "react"
import { Button, Container, Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { If, Else } from "lib/@components/switch"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { getMaintenanceCategory, getMaintenanceTask } from "../controller/maintenance-library"
import { CategoryList } from "./components/category-list"
import { EmbeddedLibraryHeader } from "./components/embedded-library-header"
import { LibrarySearchBox } from "./components/library-search-box"
import { SearchResults } from "./components/search-results"
import { TaskView } from "./components/task-view"
import { buildLibraryBreadcrumbs } from "./lib/build-library-breadcrumbs"

export function EmbeddedComponentLibraryExplorer() {
    const [navigation, setNavigation] = useState({
        categoryId: "components",
        categoryTrail: [],
        selectedTaskId: "",
        search: "",
    })
    const { categoryId, categoryTrail, selectedTaskId, search } = navigation
    const category = getMaintenanceCategory.useResults(
        { id: categoryId, excludeDrafts: true },
        withInitialValue(IS_LOADING)
    )
    const task = getMaintenanceTask.useResults(selectedTaskId || undefined)
    const refresh = useRefresh(noChange)
    const breadcrumbs = useMemo(buildBreadcrumbs, [categoryId, categoryTrail, selectedTaskId, task])

    if (category === IS_LOADING) return <LoadingScreen />

    if (!category) {
        return (
            <Container maxWidth={false} sx={{ py: 3 }}>
                <If value={categoryId === "components"}>
                    <Typography>No library data found.</Typography>
                    <Else>
                        <Typography>Category not found: {categoryId}</Typography>
                        <Button onClick={handleLibraryRoot}>Go to Library Root</Button>
                    </Else>
                </If>
            </Container>
        )
    }

    return (
        <Bound
            category={category}
            categoryId={categoryId}
            selectedTaskId={selectedTaskId}
            setCategoryId={setCategoryId}
            setSelectedTaskId={setSelectedTaskId}
            breadcrumbs={breadcrumbs}
            refresh={refresh}
            embedded
            readOnly
            canEdit={false}
            historicalMode={false}
            libraryAsOf=""
            libraryTaskVersion=""
            libraryActions={{ onNavigateBack: handleNavigateBack }}
        >
            <Container maxWidth={false} sx={{ py: 3 }}>
                <Stack spacing={3} sx={{ mb: 3 }}>
                    <EmbeddedLibraryHeader />
                    <If value={!selectedTaskId}>
                        <LibrarySearchBox value={search} onChange={handleSearchChange} placeholder="Search tasks..." />
                    </If>
                </Stack>

                <If value={selectedTaskId}>
                    <TaskView />
                    <Else>
                        <If value={search}>
                            <SearchResults search={search} categoryId={categoryId} />
                            <Else>
                                <CategoryList />
                            </Else>
                        </If>
                    </Else>
                </If>
            </Container>
        </Bound>
    )

    function buildBreadcrumbs() {
        return buildLibraryBreadcrumbs({ categoryId, categoryTrail, selectedTaskId, task })
    }

    function setCategoryId(id) {
        const nextCategoryId = id || "components"
        const child = category?.children?.find(isNextCategory)
        const trailIndex = categoryTrail.findIndex(isTrailCategory)
        if (nextCategoryId.toLowerCase() === "components") {
            setNavigation(() => ({ categoryId: "components", categoryTrail: [], selectedTaskId: "", search: "" }))
            return
        }
        if (trailIndex >= 0) {
            setNavigation((previous) => ({
                ...previous,
                categoryId: nextCategoryId,
                categoryTrail: previous.categoryTrail.slice(0, trailIndex + 1),
                selectedTaskId: "",
                search: "",
            }))
            return
        }
        if (child) {
            setNavigation((previous) => ({
                ...previous,
                categoryId: child.id,
                categoryTrail: [...previous.categoryTrail, { id: child.id, name: child.name }],
                selectedTaskId: "",
                search: "",
            }))
            return
        }

        openSearchCategory(nextCategoryId)

        function isTrailCategory(trailCategory) {
            return trailCategory.id === nextCategoryId
        }

        function isNextCategory(candidate) {
            return candidate.id === nextCategoryId
        }
    }

    async function openSearchCategory(categoryId) {
        const nextCategory = await getMaintenanceCategory({ id: categoryId, excludeDrafts: true })
        if (!nextCategory) return

        const categoryTrail = await getCategoryTrail(nextCategory)
        setNavigation({ categoryId: nextCategory.id, categoryTrail, selectedTaskId: "", search: "" })
    }

    async function getCategoryTrail(category) {
        const trail = []
        let current = category
        while (current?.id && current.id.toLowerCase() !== "components") {
            trail.unshift({ id: current.id, name: current.name })
            if (!current.parentId || current.parentId.toLowerCase() === "components") break
            current = await getMaintenanceCategory({ id: current.parentId, excludeDrafts: true })
        }
        return trail
    }

    function setSelectedTaskId(id) {
        setNavigation(selectTask)

        function selectTask(previous) {
            return { ...previous, selectedTaskId: id || "", search: "" }
        }
    }

    function handleSearchChange(value) {
        setNavigation(updateSearch)

        function updateSearch(previous) {
            return { ...previous, search: value }
        }
    }

    function handleNavigateBack() {
        setNavigation(navigateBack)

        function navigateBack(previous) {
            if (previous.selectedTaskId) return { ...previous, selectedTaskId: "" }

            const categoryTrail = previous.categoryTrail.slice(0, -1)
            const parentCategory = categoryTrail.at(-1)
            return {
                ...previous,
                categoryId: parentCategory?.id || "components",
                categoryTrail,
                search: "",
            }
        }
    }

    function handleLibraryRoot() {
        setCategoryId("components")
    }
}
